import { Box, Skeleton, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'

import classes from '../Campaign.module.scss'
import { SuggestedRow } from '../../Home/SuggestedRow/SuggestedRow'
import { SCREEN_WIDTH_BREAKPOINT } from '../../Home/SuggestedOpportunitiesRow/useSuggestedOpportunitiesRow'

import { OpportunityCard } from '@percent/workplace-giving/common/components/OpportunityCard/OpportunityCard'
import { OpportunityCardSkeleton } from '@percent/workplace-giving/common/components/OpportunityCard/OpportunityCardSkeleton'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS,
  useWindowDimensions
} from '@percent/workplace-giving/common/hooks'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import type { CampaignOpportunitiesProps } from './CampaignOpportunities.types'

export function CampaignOpportunities({ campaignId, data, isFetching }: CampaignOpportunitiesProps) {
  const { t } = useTranslation()
  const cityCampaignFiltersEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.WPG_CITY_CAMPAIGN_FILTERS)
  const { width } = useWindowDimensions()

  if (isFetching) {
    return (
      <Box className={classes.SectionWrapper} data-testid="campaign-opportunities-section-loader">
        <Skeleton height="2rem" width="55%" />
        <Box className={classes.SectionTilesContentWrapper}>
          <OpportunityCardSkeleton />
        </Box>
      </Box>
    )
  }

  if (!data?.pages?.[0]?.length) {
    return null
  }

  const opportunities = data.pages.flatMap(p => p)

  if (cityCampaignFiltersEnabled) {
    const tilesAmount = width > SCREEN_WIDTH_BREAKPOINT ? 4 : 3
    const cappedOpportunities = opportunities.slice(0, tilesAmount)

    return (
      <SuggestedRow
        testId="campaign-opportunities-section"
        title={t('workplace_giving.campaigns.upcomingOpportunities')}
        seeAllHref={{
          pathname: AppRoute.VOLUNTEERING,
          search: `?${new URLSearchParams({ campaignId }).toString()}`
        }}
        items={cappedOpportunities}
        keyExtractor={o => o.id}
        renderItem={o => <OpportunityCard opportunity={o} linkAdditionalParams={new URLSearchParams({ campaignId })} />}
      />
    )
  }

  return (
    <Box className={classes.SectionWrapper} data-testid="campaign-opportunities-section">
      <Text className={classes.SectionTitle}>{t('workplace_giving.campaigns.upcomingOpportunities')}</Text>
      <Box className={classes.SectionTilesContentWrapper}>
        {opportunities.map(o => (
          <OpportunityCard key={o.id} opportunity={o} linkAdditionalParams={new URLSearchParams({ campaignId })} />
        ))}
      </Box>
    </Box>
  )
}
